/**
 * 💍 RINGS
 *
 * Ring/accessory equipment items (15 total)
 * Level requirements: 5-90 (increments of 5)
 * Stat requirements: LUK for crit items, INT for magical rings
 *
 * Level Distribution:
 * - Common (3): Lv 5, 10, 15
 * - Uncommon (3): Lv 20, 25, 30
 * - Rare (3): Lv 35, 40, 45
 * - Epic (3): Lv 50, 55, 60
 * - Legendary (3): Lv 70, 80, 90
 *
 * Note: Uses 'ring1' slot in PlayerPanel (can equip in ring1 or ring2)
 */

export const RINGS = {
  // ============================================================
  // COMMON RINGS (3) - Lv 5, 10, 15
  // ============================================================
  copperRing: {
    id: 'copperRing',
    name: 'Copper Ring',
    icon: '💍',
    slot: 'ring1',
    rarity: 'common',
    levelReq: 5,
    requirements: {},
    description: 'A simple band of copper.',
    stats: {
      critChance: 0.02,
      health: 5,
    },
  },
  glassOrb: {
    id: 'glassOrb',
    name: 'Glass Orb',
    icon: '🔮',
    slot: 'ring1',
    rarity: 'common',
    levelReq: 10,
    requirements: { INT: 8 },
    description: 'A fragile orb with faint magical energy.',
    stats: {
      critChance: 0.03,
      health: 3,
    },
  },
  woodenCharm: {
    id: 'woodenCharm',
    name: 'Wooden Charm',
    icon: '📿',
    slot: 'ring1',
    rarity: 'common',
    levelReq: 15,
    requirements: { LUK: 12 },
    description: 'A lucky charm carved from oak.',
    stats: {
      critChance: 0.01,
      health: 8,
    },
  },

  // ============================================================
  // UNCOMMON RINGS (3) - Lv 20, 25, 30
  // ============================================================
  silverRing: {
    id: 'silverRing',
    name: 'Silver Ring',
    icon: '💍',
    slot: 'ring1',
    rarity: 'uncommon',
    levelReq: 20,
    requirements: { LUK: 30 },
    description: 'A ring of polished silver.',
    stats: {
      critChance: 0.05,
      critDamage: 0.12,
      health: 12,
    },
  },
  crystalPendant: {
    id: 'crystalPendant',
    name: 'Crystal Pendant',
    icon: '💎',
    slot: 'ring1',
    rarity: 'uncommon',
    levelReq: 25,
    requirements: { INT: 38 },
    description: 'A pendant with a glowing crystal.',
    stats: {
      critChance: 0.06,
      critDamage: 0.10,
      health: 15,
    },
  },
  runeStone: {
    id: 'runeStone',
    name: 'Rune Stone',
    icon: '🔮',
    slot: 'ring1',
    rarity: 'uncommon',
    levelReq: 30,
    requirements: { INT: 35, LUK: 25 },
    description: 'Carved with ancient protective runes.',
    stats: {
      critChance: 0.04,
      critDamage: 0.15,
      health: 10,
    },
  },

  // ============================================================
  // RARE RINGS (3) - Lv 35, 40, 45
  // ============================================================
  goldRing: {
    id: 'goldRing',
    name: 'Gold Ring',
    icon: '💍',
    slot: 'ring1',
    rarity: 'rare',
    levelReq: 35,
    requirements: { LUK: 60 },
    description: 'Pure gold infused with magic.',
    stats: {
      critChance: 0.09,
      critDamage: 0.28,
      health: 25,
    },
  },
  sapphireAmulet: {
    id: 'sapphireAmulet',
    name: 'Sapphire Amulet',
    icon: '💎',
    slot: 'ring1',
    rarity: 'rare',
    levelReq: 40,
    requirements: { INT: 70 },
    description: 'A brilliant blue gemstone of power.',
    stats: {
      critChance: 0.10,
      critDamage: 0.25,
      health: 28,
    },
  },
  arcaneBeads: {
    id: 'arcaneBeads',
    name: 'Arcane Beads',
    icon: '📿',
    slot: 'ring1',
    rarity: 'rare',
    levelReq: 45,
    requirements: { INT: 60, LUK: 40 },
    description: 'Prayer beads charged with mystical energy.',
    stats: {
      critChance: 0.08,
      critDamage: 0.30,
      health: 22,
    },
  },

  // ============================================================
  // EPIC RINGS (3) - Lv 50, 55, 60
  // ============================================================
  dragonEye: {
    id: 'dragonEye',
    name: 'Dragon Eye',
    icon: '👁️',
    slot: 'ring1',
    rarity: 'epic',
    levelReq: 50,
    requirements: { LUK: 95, INT: 40 },
    description: 'The eye of an ancient dragon, still watching.',
    stats: {
      critChance: 0.15,
      critDamage: 0.55,
      health: 48,
    },
  },
  starFragment: {
    id: 'starFragment',
    name: 'Star Fragment',
    icon: '⭐',
    slot: 'ring1',
    rarity: 'epic',
    levelReq: 55,
    requirements: { INT: 110 },
    description: 'A piece of a fallen star.',
    stats: {
      critChance: 0.16,
      critDamage: 0.50,
      health: 52,
    },
  },
  voidCrystal: {
    id: 'voidCrystal',
    name: 'Void Crystal',
    icon: '🌑',
    slot: 'ring1',
    rarity: 'epic',
    levelReq: 60,
    requirements: { LUK: 85, INT: 60 },
    description: 'Crystallized essence of the void.',
    stats: {
      critChance: 0.14,
      critDamage: 0.60,
      health: 45,
    },
  },

  // ============================================================
  // LEGENDARY RINGS (3) - Lv 70, 80, 90
  // ============================================================
  heartOfCosmos: {
    id: 'heartOfCosmos',
    name: 'Heart of Cosmos',
    icon: '💫',
    slot: 'ring1',
    rarity: 'legendary',
    levelReq: 70,
    requirements: { LUK: 140, INT: 70 },
    description: 'The beating heart of the universe itself.',
    stats: {
      critChance: 0.22,
      critDamage: 1.20,
      health: 85,
    },
  },
  ringOfEternity: {
    id: 'ringOfEternity',
    name: 'Ring of Eternity',
    icon: '💍',
    slot: 'ring1',
    rarity: 'legendary',
    levelReq: 80,
    requirements: { LUK: 160 },
    description: 'Grants power beyond mortal comprehension.',
    stats: {
      critChance: 0.25,
      critDamage: 1.00,
      health: 92,
    },
  },
  divineRelic: {
    id: 'divineRelic',
    name: 'Divine Relic',
    icon: '✨',
    slot: 'ring1',
    rarity: 'legendary',
    levelReq: 90,
    requirements: { LUK: 150, INT: 100 },
    description: 'A relic touched by the gods.',
    stats: {
      critChance: 0.20,
      critDamage: 1.40,
      health: 78,
    },
  },
};
